import React from 'react';
import Dropdown from './ui/dropdown';

interface ScheduleProps {
    cycle: 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-yearly' | 'Yearly' | 'Ad-hoc' | '';
    value: string;
    onChange: (value: string) => void;
}

const weekDays = [
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' },
    { label: 'Sunday', value: 'Sunday' },
];

const getSuffix = (n: number): string => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    const lastDigit = n % 10;
    return ['th', 'st', 'nd', 'rd'][lastDigit] || 'th';
};

const monthDays = Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}${getSuffix(i + 1)}`, value: `${i + 1}` }));
const quarters = [
    { label: 'Q1 (Jan, Feb, Mar)', value: 'Q1' },
    { label: 'Q2 (Apr, May, Jun)', value: 'Q2' },
    { label: 'Q3 (Jul, Aug, Sep)', value: 'Q3' },
    { label: 'Q4 (Oct, Nov, Dec)', value: 'Q4' },
];
const months = [
    { label: 'January', value: 'January' },
    { label: 'February', value: 'February' },
    { label: 'March', value: 'March' },
    { label: 'April', value: 'April' },
    { label: 'May', value: 'May' },
    { label: 'June', value: 'June' },
    { label: 'July', value: 'July' },
    { label: 'August', value: 'August' },
    { label: 'September', value: 'September' },
    { label: 'October', value: 'October' },
    { label: 'November', value: 'November' },
    { label: 'December', value: 'December' },
];

const getMaxDays = (month: string) => {
    const monthIndex = months.findIndex(m => m.value === month);
    if (monthIndex === -1) return 31;
    const year = new Date().getFullYear();
    return new Date(year, monthIndex + 1, 0).getDate();
};

const parseFormattedDate = (formatted: string) => {
    return formatted.replace(/[^\d]/g, '');
};

const Schedule: React.FC<ScheduleProps> = ({ cycle, value, onChange }) => {
    const [day, setDay] = React.useState<string>('');
    const [date, setDate] = React.useState<string>('');
    const [quarterly, setQuarterly] = React.useState<{ quarter: string, date: string }>({ quarter: '', date: '' });
    const [year, setYear] = React.useState<{ month: string, date: string }>({ month: '', date: '' });

    React.useEffect(() => {
        if (typeof value === 'string') {
            if (value.startsWith('weekly-')) {
                setDay(value.replace('weekly-', ''));
            } else if (value.startsWith('fortnightly-')) {
                setDay(value.replace('fortnightly-', ''));
            } else if (value.startsWith('monthly-')) {
                setDate(value.replace('monthly-', ''));
            } else if (value.startsWith('quarterly-')) {
                const parts = value.replace('quarterly-', '').split('-');
                if (parts.length === 2) {
                    setQuarterly({ quarter: parts[0], date: parseFormattedDate(parts[1]) });
                }
            } else if (value.startsWith('half-yearly-')) {
                const parts = value.replace('half-yearly-', '').split('-');
                if (parts.length === 2) {
                    setYear({ month: parts[0], date: parseFormattedDate(parts[1]) });
                }
            }else if (value.startsWith('yearly-')) {
                const parts = value.replace('yearly-', '').split('-');
                if (parts.length === 2) {
                    setYear({ month: parts[0], date: parseFormattedDate(parts[1]) });
                }
            }
        } else {
            // Reset all state when value is empty or undefined
            setDay('');
            setDate('');
            setQuarterly({ quarter: '', date: '' });
            setYear({ month: '', date: '' });
        }
    }, [value]);

    // Reset state when cycle changes, but only if value is empty
    React.useEffect(() => {
        if (!value || value === '') {
            setDay('');
            setDate('');
            setQuarterly({ quarter: '', date: '' });
            setYear({ month: '', date: '' });
        }
    }, [cycle, value]);

    if (cycle === 'Weekly') {
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={weekDays}
                        value={day}
                        onChange={(val: string) => {onChange('weekly-' + val); setDay(val);}}
                        placeholder="Select day"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    if (cycle === 'Fortnightly') {
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={weekDays}
                        value={day}
                        onChange={(val: string) => {onChange('fortnightly-' + val); setDay(val);}}
                        placeholder="Select day"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    if (cycle === 'Monthly') {
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={monthDays}
                        value={date}
                        onChange={(val: string) => {onChange('monthly-' + val); setDate(val);}}
                        placeholder="Select date"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    if (cycle === 'Quarterly') {
        const handleChange = (key: 'quarter' | 'date', val: string) => {
            const newQuarterly = { ...quarterly, [key]: val };
            setQuarterly(newQuarterly);
            
            const formattDate = (date: string) => {
                return `${date}${getSuffix(parseInt(date))}`
            };
            
            // Only call onChange if both quarter and date are selected
            if (newQuarterly.quarter && newQuarterly.date) {
                onChange(`quarterly-${newQuarterly.quarter}-${formattDate(newQuarterly.date)}`);
            }
        }
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <Dropdown
                        options={quarters}
                        value={quarterly.quarter}
                        onChange={(val: string) => handleChange('quarter', val)}
                        placeholder="Select quarter"
                        height={40}
                    />
                </div>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={monthDays}
                        value={quarterly.date}
                        onChange={(val: string) => handleChange('date', val)}
                        placeholder="Select date"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    if (cycle === 'Half-yearly') {
        const halfYears = [
            { label: 'H1 (Jan, Feb, Mar, Apr, May, Jun)', value: 'H1' },
            { label: 'H2 (Jul, Aug, Sep, Oct, Nov, Dec)', value: 'H2' },
        ];

        const handleChange = (key: 'half' | 'date', val: string) => {
            const newYear = { ...year, [key === 'half' ? 'month' : 'date']: val };
            setYear(newYear);
            
            const formattDate = (date: string) => {
                return `${date}${getSuffix(parseInt(date))}`
            };
            
            // Only call onChange if both half and date are selected
            if (newYear.month && newYear.date) {
                onChange(`half-yearly-${newYear.month}-${formattDate(newYear.date)}`);
            }
        }

        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <Dropdown
                        options={halfYears}
                        value={year.month}
                        onChange={(val: string) => handleChange('half', val)}
                        placeholder="Select half"
                        height={40}
                    />
                </div>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={monthDays}
                        value={year.date}
                        onChange={(val: string) => handleChange('date', val)}
                        placeholder="Select date"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    if (cycle === 'Yearly') {
        // For yearly, always show 29 days for February, otherwise show max days for the month
        const getYearlyMaxDays = (month: string) => {
            if (month === 'February') {
                return 29; // Always show 29 days for February in yearly recurrence
            }
            return getMaxDays(month);
        };
        const dateOptions = year.month ? Array.from({ length: getYearlyMaxDays(year.month) }, (_, i) => ({ label: `${i + 1}${getSuffix(i + 1)}`, value: `${i + 1}` })) : monthDays;

        const handleChange = (key: 'month' | 'date', val: string) => {
            const newYear = { ...year, [key]: val };
            if (key === 'month') {
                const max = getMaxDays(val);
                if (newYear.date && parseInt(newYear.date) > max) {
                    newYear.date = max.toString();
                }
            }
            setYear(newYear);
            
            const formattDate = (date: string) => {
                return `${date}${getSuffix(parseInt(date))}`
            };
            
            // Only call onChange if both month and date are selected
            if (newYear.month && newYear.date) {
                onChange(`yearly-${newYear.month}-${formattDate(newYear.date)}`);
            }
        }
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>Every</span>
                <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <Dropdown
                        options={months}
                        value={year.month}
                        onChange={(val: string) => handleChange('month', val)}
                        placeholder="Select month"
                        height={40}
                    />
                </div>
                <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Dropdown
                        options={dateOptions}
                        value={year.date}
                        onChange={(val: string) => handleChange('date', val)}
                        placeholder="Select date"
                        height={40}
                    />
                </div>
            </div>
        );
    }
    return null;
};

export default Schedule;
